// =============================================================================
// FILE:
// frontend/src/modules/pms/material_logistics/components/CreateDispatchDrawer.js
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  X,
  Maximize2,
  Minimize2
} from "lucide-react";

import {
  createDispatchHeader
} from "../services/materialDispatchService";

import {
  getProjects
} from "../../services/projectService";

import {
  getProjectSiteStores
} from "../../services/projectSiteStoreService";

// =============================================================================
// COMPONENT
// =============================================================================

export default function CreateDispatchDrawer({

  open,

  onClose,

  onSuccess

}) {

  // ===========================================================================
  // STATE
  // ===========================================================================
  const [maximized, setMaximized] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [projectLoading, setProjectLoading] =
    useState(false);

  const [projects, setProjects] =
    useState([]);

  const [siteStoreLoading, setSiteStoreLoading] =
  useState(false);

    const [siteStores, setSiteStores] =
    useState([]);  

  const [formData, setFormData] =
    useState({

      project_id: "",

      project_site_store_id: "",

      dispatch_date: "",

      expected_delivery_date: "",

      vehicle_no: "",

      driver_name: "",

      transporter_name: "",

      remarks: ""
    });


  // ===========================================================================
  // LOAD PROJECTS
  // ===========================================================================
  const loadProjects = async () => {

    try {

      setProjectLoading(true);

      const response =
        await getProjects();

      if (response?.data?.success) {

        setProjects(
          response.data.data || []
        );
      }

    } catch (err) {

      console.error(
        "Failed to load projects",
        err
      );

    } finally {

      setProjectLoading(false);
    }
  };

  // ===========================================================================
// LOAD SITE STORES
// ===========================================================================
const loadSiteStores = async (
  projectId
) => {

  try {

    setSiteStoreLoading(true);

    const response =
      await getProjectSiteStores(
        projectId
      );

    if (response?.data?.success) {

      setSiteStores(
        response.data.data || []
      );
    }

  } catch (err) {

    console.error(
      "Failed to load site stores",
      err
    );

    setSiteStores([]);

  } finally {

    setSiteStoreLoading(false);
  }
};


  // ===========================================================================
  // INITIAL LOAD
  // ===========================================================================
  useEffect(() => {

    if (open) {

      loadProjects();
    }

  }, [open]);


  // ===========================================================================
  // HANDLE CHANGE
  // ===========================================================================
  const handleChange = (
    field,
    value
  ) => {

    setFormData((prev) => ({

      ...prev,

      [field]: value
    }));
  };


  // ===========================================================================
  // HANDLE PROJECT CHANGE
  // ===========================================================================
  const handleProjectChange = async (
  value
) => {

  setFormData((prev) => ({

    ...prev,

    project_id: value,

    project_site_store_id: ""
  }));

  setSiteStores([]);

  if (value) {

    loadSiteStores(value);
  }
};


  // ===========================================================================
  // RESET FORM
  // ===========================================================================
  const resetForm = () => {

    setFormData({

      project_id: "",

      project_site_store_id: "",

      dispatch_date: "",

      expected_delivery_date: "",

      vehicle_no: "",

      driver_name: "",

      transporter_name: "",

      remarks: ""
    });
  };


  // ===========================================================================
  // HANDLE CLOSE
  // ===========================================================================
  const handleClose = () => {

    resetForm();

    onClose();
  };


  // ===========================================================================
  // HANDLE SAVE
  // ===========================================================================
  const handleSave = async () => {

    try {

      if (!formData.project_id) {

        alert(
          "Please select project"
        );

        return;
      }

      if (!formData.dispatch_date) {

        alert(
          "Please select dispatch date"
        );

        return;
      }

      setLoading(true);

      const response =
        await createDispatchHeader(
          formData
        );

      if (response?.success) {

        alert(
          "Packing list created successfully"
        );

        resetForm();

        if (onSuccess) {

          onSuccess();
        }

        onClose();
      }

    } catch (err) {

      console.error(err);

      alert(

        err?.response?.data?.message ||

        "Failed to create packing list"
      );

    } finally {

      setLoading(false);
    }
  };


  // ===========================================================================
  // HIDE
  // ===========================================================================
  if (!open) {

    return null;
  }


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <>

      {/* ================================================================ */}
      {/* OVERLAY */}
      {/* ================================================================ */}
      <div
        style={styles.overlay}
        onClick={handleClose}
      />


      {/* ================================================================ */}
      {/* DRAWER */}
      {/* ================================================================ */}
      <div
        style={{
          ...styles.drawer,

          width: maximized
            ? "calc(100vw - 250px)"
            : "520px"
        }}
      >


        {/* ============================================================ */}
        {/* HEADER */}
        {/* ============================================================ */}
        <div style={styles.header}>


          <div>

            <div style={styles.title}>
              Create Packing List
            </div>

            <div style={styles.subTitle}>

              Create logistics packing and
              dispatch header

            </div>

          </div>


          <div style={styles.headerActions}>


            <button
              style={styles.iconBtn}
              onClick={() =>
                setMaximized(
                  !maximized
                )
              }
            >

              {maximized ? (

                <Minimize2 size={18} />

              ) : (

                <Maximize2 size={18} />

              )}

            </button>


            <button
              style={styles.iconBtn}
              onClick={handleClose}
            >

              <X size={18} />

            </button>

          </div>

        </div>


        {/* ============================================================ */}
        {/* BODY */}
        {/* ============================================================ */}
        <div style={styles.body}>


          <div
            style={{
              ...styles.formGrid,

              gridTemplateColumns:
                maximized
                  ? "1fr 1fr"
                  : "1fr"
            }}
          >


            {/* PROJECT */}
            <div style={styles.fieldGroup}>

              <label style={styles.label}>
                Project
              </label>

              <select
                style={styles.select}
                value={
                  formData.project_id
                }
                onChange={(e) =>
                  handleProjectChange(
                    e.target.value
                  )
                }
              >

                <option value="">
                  {
                    projectLoading
                      ? "Loading Projects..."
                      : "Select Project"
                  }
                </option>

                {

                  projects.map((project) => (

                    <option
                      key={project.id}
                      value={project.id}
                    >

                      {

                        project.project_name ||

                        project.name ||

                        `Project ${project.id}`
                      }

                    </option>
                  ))
                }

              </select>

            </div>


            {/* SITE STORE */}
            <div style={styles.fieldGroup}>

              <label style={styles.label}>
                Site Store
              </label>

              <select
  style={styles.select}
  value={
    formData.project_site_store_id
  }
  onChange={(e) =>
    handleChange(
      "project_site_store_id",
      e.target.value
    )
  }
>

  <option value="">

    {

      siteStoreLoading

        ? "Loading Site Stores..."

        : "Select Site Store"
    }

  </option>

  {

    siteStores.map((store) => (

      <option
        key={store.id}
        value={store.id}
      >

        {

          store.site_store_name ||

          store.warehouse_name ||

          `Store ${store.id}`
        }

      </option>
    ))
  }

</select>

            </div>


            {/* DISPATCH DATE */}
            <div style={styles.fieldGroup}>

              <label style={styles.label}>
                Dispatch Date
              </label>

              <input
                type="date"
                style={styles.input}
                value={
                  formData.dispatch_date
                }
                onChange={(e) =>
                  handleChange(
                    "dispatch_date",
                    e.target.value
                  )
                }
              />

            </div>


            {/* EXPECTED DELIVERY */}
            <div style={styles.fieldGroup}>

              <label style={styles.label}>
                Expected Delivery
              </label>

              <input
                type="date"
                style={styles.input}
                value={
                  formData.expected_delivery_date
                }
                onChange={(e) =>
                  handleChange(
                    "expected_delivery_date",
                    e.target.value
                  )
                }
              />

            </div>


            {/* VEHICLE */}
            <div style={styles.fieldGroup}>

              <label style={styles.label}>
                Vehicle No
              </label>

              <input
                type="text"
                placeholder="Enter vehicle number"
                style={styles.input}
                value={
                  formData.vehicle_no
                }
                onChange={(e) =>
                  handleChange(
                    "vehicle_no",
                    e.target.value
                  )
                }
              />

            </div>


            {/* DRIVER */}
            <div style={styles.fieldGroup}>

              <label style={styles.label}>
                Driver Name
              </label>

              <input
                type="text"
                placeholder="Enter driver name"
                style={styles.input}
                value={
                  formData.driver_name
                }
                onChange={(e) =>
                  handleChange(
                    "driver_name",
                    e.target.value
                  )
                }
              />

            </div>


            {/* TRANSPORTER */}
            <div style={styles.fieldGroup}>

              <label style={styles.label}>
                Transporter Name
              </label>

              <input
                type="text"
                placeholder="Enter transporter"
                style={styles.input}
                value={
                  formData.transporter_name
                }
                onChange={(e) =>
                  handleChange(
                    "transporter_name",
                    e.target.value
                  )
                }
              />

            </div>


            {/* REMARKS */}
            <div
              style={{
                ...styles.fieldGroup,

                gridColumn:
                  maximized
                    ? "1 / span 2"
                    : "auto"
              }}
            >

              <label style={styles.label}>
                Remarks
              </label>

              <textarea
                style={styles.textarea}
                placeholder="Enter remarks"
                value={
                  formData.remarks
                }
                onChange={(e) =>
                  handleChange(
                    "remarks",
                    e.target.value
                  )
                }
              />

            </div>

          </div>

        </div>


        {/* ============================================================ */}
        {/* FOOTER */}
        {/* ============================================================ */}
        <div style={styles.footer}>


          <button
            style={styles.cancelBtn}
            onClick={handleClose}
          >
            Cancel
          </button>


          <button
            style={{
              ...styles.submitBtn,

              opacity:
                loading ? 0.7 : 1
            }}
            onClick={handleSave}
            disabled={loading}
          >

            {

              loading
                ? "Creating..."
                : "Create Packing List"
            }

          </button>

        </div>

      </div>

    </>
  );
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  overlay: {

    position: "fixed",

    top: 0,

    left: 0,

    right: 0,

    bottom: 0,

    background:
      "rgba(0,0,0,0.35)",

    zIndex: 1000
  },

  drawer: {

    position: "fixed",

    top: 0,

    right: 0,

    height: "100vh",

    background: "#ffffff",

    zIndex: 1001,

    display: "flex",

    flexDirection: "column",

    boxShadow:
      "-4px 0 20px rgba(0,0,0,0.12)",

    transition:
      "width 0.25s ease"
  },

  header: {

    padding: 20,

    borderBottom:
      "1px solid #e5e7eb",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    background: "#ffffff",

    position: "sticky",

    top: 0,

    zIndex: 5
  },

  title: {

    fontSize: 22,

    fontWeight: 700,

    color: "#111827"
  },

  subTitle: {

    marginTop: 4,

    fontSize: 13,

    color: "#6b7280"
  },

  headerActions: {

    display: "flex",

    gap: 8
  },

  iconBtn: {

    width: 36,

    height: 36,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    background: "#ffffff",

    cursor: "pointer",

    display: "flex",

    alignItems: "center",

    justifyContent: "center"
  },

  body: {

    flex: 1,

    overflowY: "auto",

    padding: 20,

    background: "#f9fafb"
  },

  formGrid: {

    display: "grid",

    gap: 18
  },

  fieldGroup: {

    display: "flex",

    flexDirection: "column"
  },

  label: {

    display: "block",

    marginBottom: 8,

    fontSize: 13,

    fontWeight: 600,

    color: "#374151"
  },

  input: {

    width: "100%",

    height: 42,

    border:
      "1px solid #d1d5db",

    borderRadius: 8,

    padding: "0 12px",

    fontSize: 14,

    background: "#ffffff"
  },

  select: {

    width: "100%",

    height: 42,

    border:
      "1px solid #d1d5db",

    borderRadius: 8,

    padding: "0 12px",

    fontSize: 14,

    background: "#ffffff"
  },

  textarea: {

    width: "100%",

    minHeight: 120,

    border:
      "1px solid #d1d5db",

    borderRadius: 8,

    padding: 12,

    fontSize: 14,

    resize: "vertical",

    background: "#ffffff"
  },

  footer: {

    padding: 20,

    borderTop:
      "1px solid #e5e7eb",

    display: "flex",

    justifyContent: "flex-end",

    gap: 12,

    background: "#ffffff",

    position: "sticky",

    bottom: 0,

    boxShadow:
      "0 -2px 10px rgba(0,0,0,0.05)"
  },

  cancelBtn: {

    border:
      "1px solid #d1d5db",

    background: "#ffffff",

    borderRadius: 8,

    padding: "10px 18px",

    cursor: "pointer",

    fontWeight: 600
  },

  submitBtn: {

    background: "#2563eb",

    color: "#ffffff",

    border: "none",

    borderRadius: 8,

    padding: "10px 18px",

    cursor: "pointer",

    fontWeight: 600
  }
};